/**
 * IntegrationCard — tarjeta genérica de una integración (Meta, Google Analytics,
 * Google Ads…). Renderiza el formulario según un spec de campos, con probar/
 * guardar/desconectar. Los campos secretos son write-only: si se dejan vacíos al
 * guardar, el backend conserva el valor previo.
 *
 * Props:
 *   provider, title, subtitle, icon, accent
 *   fields:  [{ key, label, type:'text'|'password'|'textarea', placeholder, hint, secret }]
 *   api:     { load(username), save(username, values), test(username), remove(username) }
 *            load/save devuelven { configured, status, values, secretsConfigured,
 *                                  last_verified_at, last_error }
 *   help:    JSX opcional con instrucciones para obtener las credenciales
 *   username
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  FaCheckCircle, FaTimesCircle, FaSpinner, FaSave, FaVial, FaTrash,
  FaExclamationTriangle, FaEye, FaEyeSlash,
} from 'react-icons/fa';

export default function IntegrationCard({ provider, title, subtitle, icon, accent, fields, api, help, username }) {
  const blank = () => Object.fromEntries(fields.map((f) => [f.key, '']));

  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState(blank);
  const [reveal, setReveal] = useState({});
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [notice, setNotice] = useState(null);

  const applyStatus = useCallback((s) => {
    setStatus(s);
    // Prefill de campos NO secretos con los valores actuales; secretos siempre vacíos.
    setForm(() => {
      const next = blank();
      const vals = s?.values || {};
      fields.forEach((f) => { if (!f.secret && vals[f.key] != null) next[f.key] = vals[f.key]; });
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields]);

  const load = useCallback(async () => {
    if (!username) return;
    setLoading(true);
    setForbidden(false);
    try {
      const s = await api.load(username);
      applyStatus(s);
    } catch (e) {
      if (e.status === 403) setForbidden(true);
      else if (e.status === 404) applyStatus({ configured: false, status: 'disconnected' });
      else setNotice({ type: 'error', msg: e.message });
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [username, applyStatus]);

  useEffect(() => { load(); }, [load]);

  const onField = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSave = async () => {
    setSaving(true); setNotice(null); setTestResult(null);
    try {
      const values = {};
      fields.forEach((f) => { values[f.key] = (form[f.key] || '').trim(); });
      const s = await api.save(username, values);
      applyStatus(s);
      setNotice({ type: 'ok', msg: 'Configuración guardada.' });
    } catch (e) {
      setNotice({ type: 'error', msg: e.message });
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true); setTestResult(null); setNotice(null);
    try {
      const res = await api.test(username);
      setTestResult({ ok: true, details: res.details || {} });
      if (res.status) applyStatus(res);
    } catch (e) {
      setTestResult({ ok: false, error: e.message });
      load();
    } finally {
      setTesting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm(`¿Desconectar ${title}? Se borrarán las credenciales.`)) return;
    setSaving(true);
    try {
      await api.remove(username);
      setForm(blank());
      setStatus({ configured: false, status: 'disconnected' });
      setTestResult(null);
      setNotice({ type: 'ok', msg: `${title} desconectado.` });
    } catch (e) {
      setNotice({ type: 'error', msg: e.message });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <section className="int-card"><div className="int-loading"><FaSpinner className="int-spin" /> Cargando {title}…</div></section>;
  }
  if (forbidden) {
    return (
      <section className="int-card">
        <div className="int-card-head"><span className="int-icon" style={{ background: accent }}>{icon}</span><div><h2 className="int-card-title">{title}</h2></div></div>
        <p className="int-forbidden">Solo un administrador puede configurar esta integración.</p>
      </section>
    );
  }

  const connected = status?.configured && status?.status === 'connected';
  const hasError = status?.status === 'error';
  const secretsConfigured = status?.secretsConfigured || {};
  const anySecretConfigured = Object.values(secretsConfigured).some(Boolean);
  const canSave = fields.some((f) => (form[f.key] || '').trim()) || status?.configured;

  return (
    <section className={`int-card ${connected ? 'ok' : hasError ? 'err' : ''}`}>
      <div className="int-card-head">
        <span className="int-icon" style={{ background: accent }}>{icon}</span>
        <div className="int-card-headtext">
          <h2 className="int-card-title">{title}</h2>
          {subtitle && <p className="int-card-sub">{subtitle}</p>}
        </div>
        <span className={`int-status ${connected ? 'ok' : hasError ? 'err' : 'idle'}`}>
          <span className="int-dot" />
          {connected ? 'Conectado' : hasError ? 'Error' : status?.configured ? 'Sin verificar' : 'No conectado'}
        </span>
      </div>

      {(status?.last_verified_at || hasError) && (
        <div className="int-substatus">
          {status?.last_verified_at ? `Verificado: ${new Date(status.last_verified_at).toLocaleString('es-MX')}` : ''}
          {hasError && status?.last_error ? ` · ${status.last_error}` : ''}
        </div>
      )}

      {notice && (
        <div className={`int-notice ${notice.type}`}>
          {notice.type === 'ok' ? <FaCheckCircle /> : <FaExclamationTriangle />} {notice.msg}
        </div>
      )}

      <div className="int-fields">
        {fields.map((f) => {
          const isSecret = f.secret;
          const configuredHint = isSecret && secretsConfigured[f.key];
          const placeholder = configuredHint ? '•••••••••• (guardado — deja vacío para conservar)' : (f.placeholder || '');
          if (f.type === 'textarea') {
            return (
              <label key={f.key} className="int-field">
                <span className="int-label">{f.label}{isSecret && <em> (write-only)</em>}</span>
                <textarea className="int-input int-textarea mono" rows={4} value={form[f.key]}
                  onChange={onField(f.key)} placeholder={placeholder} spellCheck={false} autoComplete="off" />
                {f.hint && <span className="int-hint">{f.hint}</span>}
              </label>
            );
          }
          const type = f.type === 'password' && !reveal[f.key] ? 'password' : 'text';
          return (
            <label key={f.key} className="int-field">
              <span className="int-label">{f.label}{isSecret && <em> (write-only)</em>}</span>
              <div className="int-input-row">
                <input className="int-input mono" type={type} value={form[f.key]}
                  onChange={onField(f.key)} placeholder={placeholder} spellCheck={false} autoComplete="off" />
                {f.type === 'password' && (
                  <button type="button" className="int-eye" onClick={() => setReveal((r) => ({ ...r, [f.key]: !r[f.key] }))}>
                    {reveal[f.key] ? <FaEyeSlash /> : <FaEye />}
                  </button>
                )}
              </div>
              {f.hint && <span className="int-hint">{f.hint}</span>}
            </label>
          );
        })}
      </div>

      <div className="int-actions">
        <button className="int-btn ghost" onClick={handleTest} disabled={testing || !status?.configured}>
          {testing ? <><FaSpinner className="int-spin" /> Probando…</> : <><FaVial /> Probar conexión</>}
        </button>
        <button className="int-btn primary" onClick={handleSave} disabled={saving || !canSave} style={{ background: accent, borderColor: accent }}>
          {saving ? <><FaSpinner className="int-spin" /> Guardando…</> : <><FaSave /> Guardar</>}
        </button>
        {status?.configured && (
          <button className="int-btn danger" onClick={handleDisconnect} disabled={saving}><FaTrash /> Desconectar</button>
        )}
      </div>

      {testResult && (
        <div className={`int-test ${testResult.ok ? 'ok' : 'err'}`}>
          {testResult.ok ? (
            <>
              <div className="int-test-head"><FaCheckCircle /> Conexión válida</div>
              <ul className="int-test-list">
                {Object.entries(testResult.details).map(([k, v]) => (
                  <li key={k}><span className="int-test-k">{k}:</span> <strong>{String(v)}</strong></li>
                ))}
              </ul>
            </>
          ) : (
            <div className="int-test-head err"><FaTimesCircle /> {testResult.error}</div>
          )}
        </div>
      )}

      {help && <details className="int-help"><summary>¿Cómo obtener las credenciales?</summary>{help}</details>}
      {!anySecretConfigured && !status?.configured && <div className="int-spacer" />}
    </section>
  );
}
